// Sprint 7 Part 2 — timezone helpers.
//
// The Boply DB stores `bookings.start_time` as `timestamptz` (UTC under the
// hood) while `league_matches.scheduled_date` + `scheduled_time` are local
// Stockholm wall-clock values. The existing `sync_booking_to_league_match`
// trigger composes the inverse direction:
//
//   v_new_date := (NEW.start_time AT TIME ZONE 'Europe/Stockholm')::date;
//   v_new_time := (NEW.start_time AT TIME ZONE 'Europe/Stockholm')::time;
//
// This file provides the JS-side helper for the OTHER direction: given a
// Stockholm wall-clock (date + time), produce the corresponding UTC `Date`
// so we can insert into `bookings.start_time`.
//
// DST handling: the helper relies on `Intl.DateTimeFormat` with
// `timeZone: "Europe/Stockholm"` to determine the offset at the target
// moment. EU DST follows the last-Sunday-of-March → last-Sunday-of-October
// rule and Intl picks the right offset on either side. The 1-2h gap on the
// spring-forward boundary is undefined behaviour (those wall-clock times do
// not exist); the helper returns whatever Intl rolls forward to. League
// matches in those 60 minutes are vanishingly rare and not a target use case.
//
// Round-trip property: for any valid (dateStr, timeStr),
//   formatStockholm(fromStockholmWallClock(dateStr, timeStr)) === `${dateStr} ${timeStr}`
// modulo seconds padding.

export const STOCKHOLM_TZ = "Europe/Stockholm";

/**
 * Compose a Stockholm wall-clock date + time into the corresponding UTC `Date`.
 *
 * @param dateStr `"YYYY-MM-DD"`
 * @param timeStr `"HH:MM"` or `"HH:MM:SS"` (longer strings truncated to 8 chars)
 * @returns `Date` whose `.toISOString()` is the UTC moment matching the
 *          Stockholm wall-clock.
 *
 * @example
 *   fromStockholmWallClock("2026-05-15", "10:00").toISOString()
 *   //=> "2026-05-15T08:00:00.000Z" (DST active → UTC+2)
 *
 *   fromStockholmWallClock("2026-01-15", "10:00").toISOString()
 *   //=> "2026-01-15T09:00:00.000Z" (no DST → UTC+1)
 */
export function fromStockholmWallClock(dateStr: string, timeStr: string): Date {
  const time =
    timeStr.length === 5
      ? `${timeStr}:00`
      : timeStr.slice(0, 8);

  // Probe: pretend the local wall-clock is UTC, then ask Intl what that
  // moment looks like in Stockholm. The difference between the two is the
  // Stockholm offset at this date.
  const naive = new Date(`${dateStr}T${time}Z`);
  const stockholmStr = new Intl.DateTimeFormat("sv-SE", {
    timeZone: STOCKHOLM_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(naive);
  // sv-SE format is "YYYY-MM-DD HH:MM:SS"
  const stockholmAsUtc = new Date(stockholmStr.replace(" ", "T") + "Z");
  const offsetMs = naive.getTime() - stockholmAsUtc.getTime();
  return new Date(naive.getTime() + offsetMs);
}

/**
 * Add `minutes` to a `Date` and return a new `Date`. Convenience wrapper so
 * callers don't reach into `getTime()` arithmetic at the call site.
 */
export function addMinutes(d: Date, minutes: number): Date {
  return new Date(d.getTime() + minutes * 60_000);
}
